/**
 * Act 3 — T1 finance arc. Programmable Escrow composer + funding tile +
 * gate cascade + settlement + T1 reveal + Bridge A.
 */

import type { ReactElement } from "react";
import type { Act3Screen } from "@/lib/scenes/screens";
import type { SceneRenderContext } from "@/lib/scenes/act4";
import { LcComposer }            from "@/components/scenes/act3/LcComposer";
import { TariffEstimateTile }    from "@/components/scenes/shared/TariffTiles";
import { FundingTile }           from "@/components/scenes/act3/FundingTile";
import { GateCascade }           from "@/components/scenes/act3/GateCascade";
import { LcIssued }              from "@/components/scenes/act3/LcIssued";
import { SettlementSimulation }  from "@/components/scenes/act3/SettlementSimulation";
import { T1Reveal }              from "@/components/scenes/act3/T1Reveal";
import { BridgeBeat }            from "@/components/scenes/shared/BridgeBeat";
import { InvestorEvidence }      from "@/components/scenes/shared/InvestorEvidence";
import { BankerEvidence }        from "@/components/scenes/shared/BankerEvidence";
import { lcGateDecisions, pickInvoiceState } from "@/components/scene/SceneRouterHelpers";

const T1_NOTIONAL_USD = 850_000;

export const ACT3_RENDERERS: Record<Act3Screen, (ctx: SceneRenderContext) => ReactElement> = {
  "3.1": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <LcComposer contract={ev.contract} composer={ev.lcComposer} showTenor={false} />
      </div>
      <TariffEstimateTile
        cifUsd={ev.contract?.priceUsd ?? T1_NOTIONAL_USD}
        jurisdiction="UAE"
        hsCode={ev.contract?.hsCode ?? "0207.12"}
      />
      <BankerEvidence variant="lc-composer" amountUsd={T1_NOTIONAL_USD} bankBadge={null} />
    </div>
  ),
  "3.2": ({ ev }) => (
    <FundingTile
      fundingMix={ev.fundingMix}
      passiveTrustLine="Newly verified · KYB ✓ · Corporate OrbID issued · 0 prior trades"
    />
  ),
  "3.3": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <GateCascade decisions={lcGateDecisions(ev, "t1-onboarding")} settlementRef="PE-2026-00042" />
      </div>
      <BankerEvidence variant="gate-cascade" amountUsd={T1_NOTIONAL_USD} bankBadge={null} />
    </div>
  ),
  "3.4": ({ ev, graph }) => <LcIssued lc={ev.lcIssuance} graph={graph} />,
  "3.5": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <SettlementSimulation
          legs={ev.settlementLegs}
          threeFieldMatched={ev.threeFieldMatched}
          invoiceMatched={pickInvoiceState(ev, "INV-2026-0042") === "SINGLE_WINDOW_SUBMITTED" && (ev.invoiceState["INV-2026-0042"] === "MATCHED")}
        />
      </div>
      <BankerEvidence variant="settlement" amountUsd={T1_NOTIONAL_USD} bankBadge={null} />
    </div>
  ),
  "3.6": ({ ev }) => (
    <div className="flex h-full flex-col gap-3">
      <div className="flex-1 min-h-0">
        <T1Reveal
          alMadarBefore={ev.reputationBefore["orbid:5493-almadar-ae"] ?? null}
          alMadarAfter={ev.reputation["orbid:5493-almadar-ae"]       ?? null}
          auroraBefore={ev.reputationBefore["orbid:5493-aves-aurora-br"] ?? null}
          auroraAfter={ev.reputation["orbid:5493-aves-aurora-br"]       ?? null}
        />
      </div>
      <InvestorEvidence variant="reveal" notionalUsd={850_000} />
    </div>
  ),
  "3.7": () => <BridgeBeat variant="a" />,
};
